import React, { forwardRef } from 'react';
import { Transformer } from 'react-konva';
import Konva from 'konva';
import { useThemeAwareColor } from '../../../react-app/theme/ThemeProvider';

interface TransformersLayerProps {
  // Style configuration
  borderStroke?: string;
  borderStrokeWidth?: number;
  anchorStroke?: string;
  anchorStrokeWidth?: number;
  anchorFill?: string;
  anchorSize?: number;
  nodesBoundBoxFunc?: (oldBox: any, newBox: any) => any;
}

interface TransformersLayerRefs {
  nodesTransformerRef: React.RefObject<Konva.Transformer | null>;
  frameTransformerRef: React.RefObject<Konva.Transformer | null>;
}

export const TransformersLayer = forwardRef<TransformersLayerRefs, TransformersLayerProps>((
  {
    borderStroke,
    borderStrokeWidth = 1.5,
    anchorStroke,
    anchorStrokeWidth = 1,
    anchorFill,
    anchorSize = 10,
    nodesBoundBoxFunc
  },
  ref
) => {
  const getThemeColor = useThemeAwareColor();
  const nodesTransformerRef = React.useRef<Konva.Transformer>(null);
  const frameTransformerRef = React.useRef<Konva.Transformer>(null);
  
  // Expose refs to parent
  React.useImperativeHandle(ref, () => ({
    nodesTransformerRef,
    frameTransformerRef
  }));
  
  const anchorStyleFunc = (anchor: Konva.Rect) => {
    anchor.cornerRadius(50);
    anchor.fill(anchorFill || getThemeColor('#E5E7EB', { type: 'background' }));
    anchor.stroke(anchorStroke || getThemeColor('#9CA3AF', { type: 'foreground' }));
    anchor.strokeWidth(anchorStrokeWidth);

    const anyAnchor = anchor as any;
    if (!anyAnchor.__cursorEventsBound) {
      anyAnchor.__cursorEventsBound = true;
      const setCursor = (cursor: string) => {
        try {
          const stage = anchor.getStage();
          const container = stage?.container();
          if (container) {
            container.style.cursor = cursor;
            return;
          }
        } catch {}
        try {
          document.body.style.cursor = cursor;
        } catch {}
      };
      anchor.on('mouseenter', () => {
        const n = (anchor.name?.() || '') as string;
        if (n === 'top-left' || n === 'bottom-right') setCursor('nwse-resize');
        else if (n === 'top-right' || n === 'bottom-left') setCursor('nesw-resize');
      });
      anchor.on('mouseleave', () => setCursor('default'));
    }
  };
  
  return (
    <>
      {/* Transformer for selected nodes */}
      <Transformer
        ref={nodesTransformerRef}
        boundBoxFunc={(oldBox, newBox) => {
          const next = nodesBoundBoxFunc ? nodesBoundBoxFunc(oldBox, newBox) : newBox;
          if (next.width < 20 || next.height < 20) return oldBox;
          return next;
        }}
        ignoreStroke={true}
        keepRatio={false}
        rotateEnabled={false}
        enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
        anchorSize={anchorSize}
        borderStroke={borderStroke || getThemeColor('#3b82f6', { type: 'accent' })}
        borderStrokeWidth={borderStrokeWidth}
        anchorStroke={anchorStroke || getThemeColor('#9CA3AF', { type: 'foreground' })}
        anchorStrokeWidth={anchorStrokeWidth}
        anchorFill={anchorFill || getThemeColor('#E5E7EB', { type: 'background' })}
        anchorCornerRadius={50}
        anchorStyleFunc={anchorStyleFunc}
      />
      
      {/* Transformer for selected frame */}
      <Transformer
        ref={frameTransformerRef}
        rotateEnabled={false}
        boundBoxFunc={(oldBox, newBox) => {
          if (newBox.width < 80 || newBox.height < 60) return oldBox;
          return newBox;
        }}
        ignoreStroke={true}
        enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
        anchorSize={anchorSize}
        borderStroke={borderStroke || getThemeColor('#3b82f6', { type: 'accent' })}
        borderStrokeWidth={borderStrokeWidth}
        anchorStroke={anchorStroke || getThemeColor('#9CA3AF', { type: 'foreground' })}
        anchorStrokeWidth={anchorStrokeWidth}
        anchorFill={anchorFill || getThemeColor('#E5E7EB', { type: 'background' })}
        anchorCornerRadius={50}
        anchorStyleFunc={anchorStyleFunc}
      />
    </>
  );
});

TransformersLayer.displayName = 'TransformersLayer';
